import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router';
import { Space, Segmented, Tag, Typography } from 'antd';
import { TableOutlined, HistoryOutlined } from '@ant-design/icons';
import { parseAsString, useQueryState } from "nuqs";
import { ColorModeContext } from '../../contexts/color-mode';
import { CurrentReadingsTable } from './CurrentReadingsTable';
import { HistoricalReadingsTable } from './HistoricalReadingsTable';
import { MetersSearch } from './MetersSearch';

// Custom debounce hook
const useDebounce = (value: string, delay: number) => {
    const [debouncedValue, setDebouncedValue] = useState(value);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);

        return () => {
            clearTimeout(handler);
        };
    }, [value, delay]);

    return debouncedValue;
};

type ViewMode = 'current' | 'historical';
type MediaType = 'electricity' | 'all' | 'water' | 'heat';

interface MeterReadingsViewProps {
    concentratorID: string;
    media: MediaType;
    columns: any[];
    concentratorName?: string;
}

export const MeterReadingsView: React.FC<MeterReadingsViewProps> = ({
    concentratorID,
    media,
    columns,
    concentratorName,
}) => {
    const { viewMode: urlViewMode } = useParams<{ viewMode?: ViewMode }>();
    const navigate = useNavigate();
    const location = useLocation();
    const { mode } = useContext(ColorModeContext);
    const isDark = mode === 'dark';
    const [viewMode, setViewMode] = useState<ViewMode>('current');
    const [searchInput, setSearchInput] = useQueryState(
        "search",
        parseAsString.withDefault("").withOptions({ history: "replace" })
    );

    // Initialize viewMode from URL params
    useEffect(() => {
        if (media === 'all' && urlViewMode === 'historical') {
            const searchSuffix = location.search || "";
            navigate(`/concentrators/${concentratorID}/meters/${media}/current${searchSuffix}`, {
                replace: true,
            });
            setViewMode('current');
            return;
        }

        if (urlViewMode && ['current', 'historical'].includes(urlViewMode)) {
            setViewMode(urlViewMode as ViewMode);
        } else {
            setViewMode('current');
        }
    }, [concentratorID, location.search, media, navigate, urlViewMode]);

    // Debounce search input - this is what gets sent to API
    const debouncedSearchValue = useDebounce(searchInput, 300);

    const handleSearchChange = (value: string) => {
        void setSearchInput(value || null, { history: "replace" });
    };

    const handleViewModeChange = (newViewMode: ViewMode) => {
        const searchSuffix = location.search || "";
        navigate(
            `/concentrators/${concentratorID}/meters/${media}/${newViewMode}${searchSuffix}`,
            { replace: true }
        );
    };

    return (
        <div style={{ width: '100%' }}>
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Segmented
                        options={media === 'all' ? [
                            {
                                label: (
                                    <Space>
                                        <TableOutlined />
                                        Current Readings
                                    </Space>
                                ),
                                value: 'current',
                            },
                        ] : [
                            {
                                label: (
                                    <Space>
                                        <TableOutlined />
                                        Current Readings
                                    </Space>
                                ),
                                value: 'current',
                            },
                            {
                                label: (
                                    <Space>
                                        <HistoryOutlined />
                                        Historical Data
                                    </Space>
                                ),
                                value: 'historical',
                            },
                        ]}
                        value={viewMode}
                        onChange={(value) => handleViewModeChange(value as ViewMode)}
                    />
                    
                    {viewMode === 'historical' && media !== 'all' && (
                        <Space size="small" style={{ fontSize: '12px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <div style={{ 
                                    width: '12px', 
                                    height: '12px', 
                                    backgroundColor: isDark ? '#1f3a1f' : '#f6ffed', 
                                    borderRadius: '2px',
                                    border: isDark ? '1px solid #3a5a3a' : '1px solid #d9f7be'
                                }}></div>
                                <Typography.Text type="secondary" style={{ fontSize: '11px' }}>Last day</Typography.Text>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <div style={{ 
                                    width: '12px', 
                                    height: '12px', 
                                    backgroundColor: isDark ? '#3a3010' : '#fffbe6', 
                                    borderRadius: '2px',
                                    border: isDark ? '1px solid #5a5030' : '1px solid #ffe58f'
                                }}></div>
                                <Typography.Text type="secondary" style={{ fontSize: '11px' }}>2-3 days</Typography.Text>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <div style={{ 
                                    width: '12px', 
                                    height: '12px', 
                                    backgroundColor: isDark ? '#3a1a1a' : '#fff2f0', 
                                    borderRadius: '2px',
                                    border: isDark ? '1px solid #5a3030' : '1px solid #ffccc7'
                                }}></div>
                                <Typography.Text type="secondary" style={{ fontSize: '11px' }}>Earlier</Typography.Text>
                            </div>
                            <Tag color="default">No data</Tag>
                            {media === 'electricity' && (
                                <>
                                    <span style={{ margin: '0 8px', color: '#999' }}>|</span>
                                    <Typography.Text type="secondary" style={{ fontSize: '11px' }}>
                                        Top: kWh, Bottom: kVArh
                                    </Typography.Text>
                                </>
                            )}
                        </Space>
                    )}
                </div>

                {/* Shared search component */}
                <MetersSearch
                    value={searchInput}
                    onChange={handleSearchChange}
                    placeholder={`Search ${media} meters (fuzzy search supported)...`}
                />

                {urlViewMode === 'historical' && media !== 'all' ? (
                    <HistoricalReadingsTable
                        media={media}
                        concentratorID={concentratorID}
                        concentratorName={concentratorName}
                        searchValue={debouncedSearchValue}
                    />
                ) : (
                    <CurrentReadingsTable
                        concentratorID={concentratorID}
                        media={media}
                        columns={columns}
                        searchValue={debouncedSearchValue}
                    />
                )}
            </Space>
        </div>
    );
};
